import React from 'react';
import DataImporter from './index';
import { Accordion, Table } from 'react-bootstrap';
import { createForm } from '../../api/formsAPI';
import { createFormItem } from '../../api/form-itemsAPI';
import { useUserProfile } from '../../masterLayout/MasterLayoutBody';

const FormImporter = ({ onComplete }) => {
  const userProfile = useUserProfile();

  const handleDataImport = async (rows, onProgress) => {
    if (!rows.length) return;
    let currentForm = null;
    let currentName = null;
    let formItemCount = 0;
    let processedCount = 0;

    for (const row of rows) {
      if (!currentForm || row.name !== currentName) {
        currentName = row.name;
        formItemCount = 0;
        try {
          currentForm = await createForm({
            name: row.name,
            description: row.formDescription,
            formTypeId: row.formTypeId,
            image: ''
          });
        } catch (err) {
          currentForm = null;
          console.error('Failed to create form', err);
        }
      }

      if (currentForm) {
        try {
          let itemData = {};
          if (row.data) {
            try {
              itemData = JSON.parse(row.data);
            } catch (e) {
              itemData = {};
            }
          }

          await createFormItem({
            formId: currentForm.id,
            description: row.description,
            width: Number(row.width) || 12,
            heading: Number(row.header) || 0,
            formInputTypeId: row.formInputType,
            instructions: row.instructions || '',
            required: Number(row.required) || 0,
            includePhoto: Number(row.includePhoto) || 0,
            disableComments: Number(row.disabledComments) || 0,
            raiseDefects: Number(row.raiseDefects) || 0,
            sortOrder: formItemCount + 1,
            createdBy: userProfile?.id,
            data: itemData,
            images: []
          });
          formItemCount += 1;
        } catch (err) {
          console.error('Failed to import form item', err);
        }
      }

      processedCount += 1;
      if (onProgress) onProgress(processedCount);
    }
  };

  return (
    <>
      <div className='card h-100 p-16 rounded-0'>
        <p>
          You can bulk import <strong>forms</strong> into the system using an Excel or CSV file.
          This helps speed up onboarding or migration processes by avoiding manual entry.
        </p>
        <p>Expand the lists below to see the required and optional fields.</p>
        <div className="mb-4">
          <Accordion alwaysOpen={false}>
            <Accordion.Item eventKey="0">
              <Accordion.Header>Required Columns</Accordion.Header>
              <Accordion.Body>
                <p>Your file must include the following column headers:</p>
                <Table bordered size="sm">
                  <thead className="table-light">
                    <tr>
                      <th>Column Name</th>
                      <th>Type</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>name</td><td>Text</td><td>Name of the form (a new form is created when this value changes)</td></tr>
                    <tr><td>formDescription</td><td>Text</td><td>Description of the form</td></tr>
                    <tr><td>formTypeId</td><td>Lookup</td><td>Form type name</td></tr>
                    <tr><td>description</td><td>Text</td><td>Description of the form item</td></tr>
                    <tr><td>width</td><td>Number</td><td>Column width (1-12)</td></tr>
                    <tr><td>header</td><td>Number</td><td>1 if item is a header</td></tr>
                    <tr><td>formInputType</td><td>Lookup</td><td>Input type name</td></tr>
                  </tbody>
                </Table>
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="1">
              <Accordion.Header>Optional Columns</Accordion.Header>
              <Accordion.Body>
                <p>Your file can optionally include the following column headers:</p>
                <Table bordered size="sm">
                  <thead className="table-light">
                    <tr>
                      <th>Column Name</th>
                      <th>Type</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>instructions</td><td>Text</td><td>Additional instructions</td></tr>
                    <tr><td>required</td><td>Number</td><td>1 if response is required</td></tr>
                    <tr><td>includePhoto</td><td>Number</td><td>1 to allow photo upload</td></tr>
                    <tr><td>disabledComments</td><td>Number</td><td>1 to disable comments</td></tr>
                    <tr><td>raiseDefects</td><td>Number</td><td>1 to raise defect from yes/no, ok/defect or pass/fail</td></tr>
                    <tr><td>data</td><td>Text</td><td>JSON data for the item</td></tr>
                  </tbody>
                </Table>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </div>
        <DataImporter
          entity="forms"
          requiredFields={[
            { key: 'name', type: 'string', required: true },
            { key: 'formDescription', type: 'string', required: true },
            { key: 'formTypeId', type: 'lookup', required: true, api: 'form-type', nameField: 'name' },
            { key: 'description', type: 'string', required: true },
            { key: 'width', type: 'number', required: true },
            { key: 'header', type: 'number', required: true },
            { key: 'formInputType', type: 'lookup', required: true, api: 'form-input-type', nameField: 'inputType' },
            { key: 'instructions', type: 'string' },
            { key: 'required', type: 'number' },
            { key: 'raiseDefects', type: 'number' },
            { key: 'includePhoto', type: 'number' },
            { key: 'disabledComments', type: 'number' },
            { key: 'data', type: 'string' },
          ]}
          onCommit={handleDataImport}
          onComplete={onComplete}
        />
      </div>
    </>
  );
};

export default FormImporter;
